<script lang="ts" setup>
import type { Charge } from '@/services/order'
import type { ImageMap } from '@/services/productImage'
import { formatDate } from '@/lib/formatters/date'
import { formatMoneyWithCurrency } from '@/lib/formatters/money'

import Button from '@/components/Button.vue'
import BowerbirdLogo from '@/assets/images/bowerbird.svg?component'
import ProductImageBubbleList from '@/components/ProductImageBubbleList.vue'
import ChargeErrorBanner from '@/components/ChargeErrorBanner.vue'

const props = defineProps<{
  order: Charge
  imagesByProductId: Record<string, ImageMap[] | null | undefined>
  status: 'rescheduled' | 'sent-now'
}>()

const emit = defineEmits<{
  (e: 'done'): void
  (e: 'viewOrder'): void
}>()

const getImagesForProduct = (id: string) => {
  const imageList = props.imagesByProductId[id]
  if (imageList) {
    return imageList[0]
  } else {
    return undefined
  }
}
</script>

<template>
  <div class="NextOrderConfirmation">
    <div class="NextOrderConfirmation__Body">
      <div class="NextOrderConfirmation__Header">
        <BowerbirdLogo />
      </div>
      <div class="NextOrderConfirmation__Content">
        <h2>
          {{
            status === 'rescheduled'
              ? "We've rescheduled your order"
              : "We've got your order"
          }}
        </h2>
        <div class="NextOrderConfirmation__OrderMeta">
          <template v-if="status == 'rescheduled'">Processes</template>
          {{ formatDate('DD Mon', order.scheduled_at) }}
          <span>•</span>
          {{ order.line_items.reduce((acc, item) => acc + item.quantity, 0) }}
          item{{ order.line_items.length > 1 ? 's' : '' }}
          <span>•</span>
          {{ formatMoneyWithCurrency(order.total_price) }}
        </div>
        <ChargeErrorBanner v-if="order.status === 'pending'" :charge="order" />
        <p v-else>
          Your order should arrive within 3-5 days of being processed.
        </p>
      </div>
      <div class="NextOrderConfirmation__ProductImages">
        <ProductImageBubbleList
          :images="
            order.line_items.map((li) => ({
              url: getImagesForProduct(
                `${li.external_product_id.ecommerce}:${li.external_variant_id.ecommerce}`,
              )?.small,
              alt: li.title,
            }))
          "
        ></ProductImageBubbleList>
      </div>
      <div class="NextOrderConfirmation__Actions">
        <Button :block="true" @click="emit('viewOrder')">View order</Button>
      </div>
    </div>
  </div>
</template>

<style>
.NextOrder__ConfirmationContainer {
  padding: 0 20px;

  @media (--mq-landscape-tablet-and-up) {
    padding: 0;
  }
}

.NextOrderConfirmation {
  padding-top: 40px;
  overflow: hidden;
}

.NextOrderConfirmation__Body {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 15px;
  padding: 0 20px 20px;
  background-color: var(--color-white);
  border-radius: 8px;
}

.NextOrderConfirmation__Header {
  position: relative;
  border-radius: 1000px;
  background-color: var(--color-white);
  height: calc(96px + 16px);
  width: calc(96px + 16px);
  border: 8px solid var(--color-white);
  margin: -40px auto 0;

  & svg {
    position: relative;
    z-index: 2;
  }
}

.NextOrderConfirmation__Content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;

  & > h2,
  & > p {
    margin: 0;
    text-align: center;
  }
}

.NextOrderConfirmation__OrderMeta {
  font-weight: bold;

  & span {
    font-weight: normal;
    color: var(--color-forest-50);
    padding: 0 5px;
  }
}

.NextOrderConfirmation__Confetti {
  position: absolute;
  z-index: 1;
  margin-top: -325px;
  top: 50%;
  transform: translateY(-60px);
}

.NextOrderConfirmation__Confetti--left {
  right: 70%;
}

.NextOrderConfirmation__Confetti--right {
  left: 70%;
}
</style>
